/* eslint-disable react/no-did-mount-set-state, react/no-did-update-set-state */

import React from 'react';
import PropTypes from 'prop-types';
import AssociableHotspot from './AssociableHotspot';
import GapChoice from './GapChoice';
import GapChoicesContainer from './GapChoicesContainer';
import ImageDisplay from './../../media/image/ImageDisplay';
import KeyboardHelpAndPrompt from '../../keyboardHelp/KeyboardHelpAndPrompt';
import LoadTracker from '../../../../services/LoadTracker';

import styles from './dragAndDropInteraction.scss';

class GraphicGapMatchInteraction extends React.Component {
    static propTypes = {
        callback: PropTypes.func.isRequired, // callback(responseIdentifier, [simpleChoice identifiers])
        correctResponses: PropTypes.array,
        extraResponses: PropTypes.array,
        handleInteractionReady: PropTypes.func.isRequired,
        isReadOnly: PropTypes.bool,
        model: PropTypes.object.isRequired, // model taken from responseModels related to this interaction
        questionModel: PropTypes.object.isRequired,
        userResponse: PropTypes.array.isRequired, // array with simpleChoice identifiers
        settingValues: PropTypes.object,
        direction: PropTypes.string,
    };

    static getCorrectMatchesToShow(correctResponses, userResponse) {
        const responseMap = userResponse.reduce((map, item) => {
            const pair = item.split(' ');
            map[pair[0]] = pair[1];
            return map;
        }, {});

        return correctResponses
            .map((item) => {
                const pair = item.split(' ');
                return { gapChoice: pair[0], associableHotspot: pair[1] };
            })
            .filter((item) => {
                // Include gap choices with no response.
                if (responseMap[item.gapChoice] === undefined) {
                    return true;
                }

                // Include gap choices with incorrect response.
                return responseMap[item.gapChoice] !== item.associableHotspot;
            });
    }

    static getSnapHierarchy(responseArray) {
        const snapHierarchy = {};
        for (const response of responseArray) {
            const dropZoneId = response.split(' ')[1];
            const draggableId = response.split(' ')[0];

            if (snapHierarchy[dropZoneId] !== undefined) {
                snapHierarchy[dropZoneId].push(draggableId);
            } else {
                snapHierarchy[dropZoneId] = [draggableId];
            }
        }

        return snapHierarchy;
    }

    static getCssTransformedTopAndLeft(element) {
        const transformStyle = element.style.transform || element.style.webkitTransform;
        if (!transformStyle || transformStyle.indexOf('translate') !== 0) {
            return null;
        }

        const pattern = /translate\((\d+)px, (\d+)px\)/;
        const match = transformStyle.match(pattern);

        if (!match) {
            return null;
        }

        return {
            left: parseInt(match[1], 10),
            top: parseInt(match[2], 10),
        };
    }

    static getElementCenterCoords(element) {
        let coords = { top: 0, left: 0 };
        if (element.style.transform || element.style.webkitTransform) {
            const transformed = GraphicGapMatchInteraction.getCssTransformedTopAndLeft(element);
            coords = transformed || coords;
        } else {
            coords.top = element.offsetTop;
            coords.left = element.offsetLeft;
        }

        coords.top += (Math.round(element.clientHeight / 2));
        coords.left += (Math.round(element.clientWidth / 2));

        return coords;
    }


    constructor(props, context) {
        super(props, context);
        this.mainElements = {};
        const stepSize = 1;
        this.updateStepSize = this.updateStepSize.bind(this);
        const snapHierarchy = props.model.freePlacing ? undefined : GraphicGapMatchInteraction.getSnapHierarchy(props.userResponse);
        this.state = Object.assign({ snapHierarchy, stepSize }, this.getMaps(props));
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.setMediaTracker();
        this.trackBackgroundImage();
    }

    componentDidMount() {
        this.dealWithElements();
        this.mediaTracker.setAllItemsAdded();
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(newProps) {
        const stateUpdate = this.getMaps(newProps);
        if (newProps.userResponse !== this.props.userResponse && !newProps.model.freePlacing) {
            stateUpdate.snapHierarchy = GraphicGapMatchInteraction.getSnapHierarchy(newProps.userResponse);
        }
        this.setState(stateUpdate);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate() {
        this.setMediaTracker();
    }

    componentDidUpdate() {
        this.dealWithElements();
        if (this.props.correctResponses && this.containerElementsAreReady()) {
            this.renderCorrectResponsesLines();
        }

        if (this.state.resetGapChoice) {
            this.setState({ resetGapChoice: undefined });
        }

        this.mediaTracker.setAllItemsAdded();
    }

    dealWithElements() {
        const stateUpdates = {};
        const elms = ['linesCanvasElement', 'gapChoicesContainerElement', 'wrapperElement', 'backgroundContainerElement'];
        elms.forEach((elm) => {
            if (this.mainElements[elm] !== this.state[elm]) {
                stateUpdates[elm] = this.mainElements[elm];
            }
        });

        if (Object.keys(stateUpdates).length > 0) {
            this.setState(stateUpdates);
        }
    }

    updateStepSize(newStepSize) {
        this.setState({ stepSize: newStepSize });
    }

    setMediaTracker() {
        this.mediaTracker = new LoadTracker(
            (success) => {
                this.props.handleInteractionReady(success);
            },
            `Interaction ${this.props.model.identifier}/images`
        );
    }

    trackBackgroundImage() {
        const { backgroundImageModel } = this.props.model;
        if (backgroundImageModel && backgroundImageModel.url) {
            this.onBackgroundImageReady = this.mediaTracker.addItemToTrack(`Media ${backgroundImageModel.url}`);
        }
    }

    /**
     * @return {Array} Array of { fromCoords: { top: x, left: y } , toCoords: { top: X, left: Y } }
     */
    getCorrectResponsesLinesCoords(linesToDraw) {
        return linesToDraw.map((pair) => {
            const idPrefix = this.getIdPrefix();
            const gapChoiceElement = document.getElementById(`${idPrefix}-GAP_CHOICE-${pair.gapChoice}`);
            const associableHotspotElement = document.getElementById(`${idPrefix}-ASSOCIABLE_HOTSPOT-${pair.associableHotspot}`);
            const fromCoords = GraphicGapMatchInteraction.getElementCenterCoords(gapChoiceElement);
            const toCoords = GraphicGapMatchInteraction.getElementCenterCoords(associableHotspotElement);

            return { fromCoords, toCoords };
        });
    }

    getIdPrefix() {
        return `QUESTION-${this.props.questionModel.contentRevisionId}-RESPONSE-${this.props.model.identifier}`;
    }

    getLinesCanvasSize() {
        if (this.wrapperElement) {
            return {
                width: parseInt(this.wrapperElement.clientWidth, 10),
                height: parseInt(this.wrapperElement.clientHeight, 10),
            };
        }

        return null;
    }

    getMaps(newProps) {
        const { extraResponses, model, userResponse } = newProps;
        const maps = {
            droppedGapChoices: [],
            notDropped: [],
            associableHotspotMap: {},
            gapChoiceMap: {},
            gapChoiceToAssociableHotspotMap: {}, // Key = gapChoice.identifier, value = gap.identifier
            associableHotspotToGapChoiceMap: {}, // Key = gap.identifier, value = gapChoice.identifier
        };
        const inResponse = userResponse
            .map(item => item.split(' ')[0])
            .concat(
                extraResponses
                    .filter((extraResponse) => {
                        const key = Object.keys(extraResponse)[0];
                        return extraResponse[key].length > 0;
                    })
                    .map(extraResponse => Object.keys(extraResponse)[0])
            );

        userResponse.forEach((item) => {
            const match = item.split(' ');
            maps.gapChoiceToAssociableHotspotMap[match[0]] = match[1];
            maps.associableHotspotToGapChoiceMap[match[1]] = match[0];
        });

        model.associableHotspots.forEach((gapModel) => {
            maps.associableHotspotMap[gapModel.identifier] = gapModel;
        });

        model.gapChoiceModels.forEach((gapChoiceModel) => {
            maps.gapChoiceMap[gapChoiceModel.identifier] = gapChoiceModel;

            if (inResponse.indexOf(gapChoiceModel.identifier) !== -1) {
                maps.droppedGapChoices.push(gapChoiceModel);
            } else {
                maps.notDropped.push(gapChoiceModel);
            }
        });

        return maps;
    }

    getUnPositionedGapChoices() {
        return this.props.model.gapChoiceModels
            .filter(gapChoiceModel => !gapChoiceModel.isPositioned);
    }

    handleGapChoiceMovedBack = (identifier) => {
        const { callback, model, userResponse } = this.props;
        const newUserResponse = userResponse.slice(0)
            .filter(item => item.split(' ')[0] !== identifier);
        const newExtraResponses = [{
            identifier,
            cardinality: 'single',
            baseType: 'point',
            candidateResponse: [],
        }];

        setTimeout(() => {
            callback(model.identifier, newUserResponse, newExtraResponses);
        }, 50); // Wait just a few ms to let Draggable setState() before we kill it by re rendering.
    };

    handleUserResponse = (el, leftOffset, topOffset) => {
        const identifier = el.getAttribute('data-identifier');
        if (leftOffset === undefined) {
            this.handleGapChoiceMovedBack(identifier);
            return;
        }

        // Gets the center coordinates of the element
        const xElementCenter = leftOffset + (el.offsetWidth / 2);
        const yElementCenter = topOffset + (el.offsetHeight / 2);
        const model = this.props.model;
        const response = this.props.userResponse.slice(0);
        const extraResponses = [];

        // Store the new coordinates of the dragged element
        extraResponses.push({
            identifier,
            cardinality: 'single',
            baseType: 'point',
            candidateResponse: [`${leftOffset} ${topOffset}`], // e.g. ['20 166']
        });

        for (const associableHotspot of model.associableHotspots) {
            const coords = associableHotspot.coords.split(',');
            const responseIndex = response.indexOf(`${identifier} ${associableHotspot.identifier}`);

            const droppedInDropZone = xElementCenter >= parseInt(coords[0], 10) &&
                xElementCenter <= parseInt(coords[2], 10) &&
                yElementCenter >= parseInt(coords[1], 10) &&
                yElementCenter <= parseInt(coords[3], 10);

            // Check if center of dragged element is within the drop zone and push it to the user's response
            if (droppedInDropZone &&
                responseIndex < 0) {
                response.push(`${identifier} ${associableHotspot.identifier}`);
            } else if (responseIndex > -1 && !droppedInDropZone) {
                response.splice(responseIndex, 1);
            }
        }

        setTimeout(() => {
            this.props.callback(model.identifier, response, extraResponses);
        }, 50); // Wait just a few ms to let Draggable setState() before we kill it by re rendering.
    };

    handleGapChoicesContainerReady = (element) => {
        this.mainElements.gapChoicesContainerElement = element;
    };

    renderCorrectResponsesLines() {
        if (!this.linesCanvasElement) {
            return;
        }
        const { correctResponses, userResponse } = this.props;
        const correctMatches = GraphicGapMatchInteraction.getCorrectMatchesToShow(correctResponses, userResponse);
        const coordPairs = this.getCorrectResponsesLinesCoords(correctMatches);
        const { width, height } = this.getLinesCanvasSize();
        if (width && this.linesCanvasElement.width !== width) {
            this.linesCanvasElement.width = width;
            this.linesCanvasElement.height = height;
        }

        const context = this.linesCanvasElement.getContext('2d');
        context.clearRect(0, 0, this.linesCanvasElement.width, this.linesCanvasElement.height);
        context.lineWidth = 2;
        context.strokeStyle = '#999999';

        coordPairs.forEach((pair) => {
            const { fromCoords, toCoords } = pair;
            context.beginPath();
            context.moveTo(fromCoords.left, fromCoords.top);
            context.lineTo(toCoords.left, toCoords.top);
            context.stroke();
        });
    }

    renderGapChoicesOnCanvas() {
        const { extraResponses, model } = this.props;

        return model.gapChoiceModels.map((gapChoiceModel) => {
            const extraResponse = extraResponses.find(extraResp =>
                extraResp[gapChoiceModel.identifier] !== undefined
            );

            return (<GapChoice
                gapChoiceModel={gapChoiceModel}
                extraResponse={extraResponse}
                handleUserResponse={this.handleUserResponse}
                idPrefix={this.getIdPrefix()}
                key={`${this.getIdPrefix()}-GAP_CHOICE-${gapChoiceModel.identifier}`}
                mediaTracker={this.mediaTracker}
                snapHierarchy={this.state.snapHierarchy}
                updateStepSize={this.updateStepSize}
                {...this.props}
                {...this.state}
            />);
        });
    }

    containerElementsAreReady() {
        const { backgroundContainerElement, gapChoicesContainerElement, linesCanvasElement, wrapperElement } = this.mainElements;
        return wrapperElement
            && backgroundContainerElement
            && (this.shouldIncludeCorrectResponses() ? linesCanvasElement : true)
            && (this.shouldIncludeGapChoicesContainer() ? gapChoicesContainerElement : true);
    }

    shouldIncludeGapChoicesContainer() {
        return this.getUnPositionedGapChoices().length > 0;
    }

    shouldIncludeCorrectResponses() {
        return !!this.props.correctResponses;
    }

    renderGapChoicesContainer() {
        const unPositionedGapChoices = this.getUnPositionedGapChoices();

        return (<GapChoicesContainer
            gapChoiceModels={unPositionedGapChoices}
            mediaTracker={this.mediaTracker}
            onElement={this.handleGapChoicesContainerReady}
        />);
    }

    render() {
        const { model, direction, correctResponses, isReadOnly, settingValues = {} } = this.props;
        const { backgroundImageModel = { width: 620, height: model.canvasHeight || 400 } } = model;
        const hasBackgroundImage = backgroundImageModel && backgroundImageModel.url;
        const canvasStyle = {
            height: `${backgroundImageModel.height}px`,
            width: `${backgroundImageModel.width}px`,
        };

        const wrapperStyle = { width: `${backgroundImageModel.width + 2}px` };

        return (
            <div
                className="interaction"
                ref={(element) => { this.rootElement = element; }}
                dir={direction}
            >
                <KeyboardHelpAndPrompt
                    captionKey="interactions.dragAndDrop.keyboardInteractionHelpText"
                    model={model}
                    showAccessibilityHelpers={settingValues.show_accessibility_helpers && !isReadOnly}
                />

                <div
                    className={styles.wrapper}
                    id={`${this.getIdPrefix()}-draggable-area-wrapper`}
                    ref={(element) => { this.mainElements.wrapperElement = element; }}
                    style={wrapperStyle}
                >
                    {
                        this.shouldIncludeCorrectResponses() ?
                            <canvas
                                className={styles.connectionsCanvas}
                                height={backgroundImageModel.height}
                                width={backgroundImageModel.width}
                                ref={(element) => { this.mainElements.linesCanvasElement = element; }}
                            /> : null
                    }

                    <div
                        ref={(element) => { this.mainElements.backgroundContainerElement = element; }}
                        className={styles.backgroundContainer}
                        style={canvasStyle}
                    >
                        {
                            hasBackgroundImage ?
                                <div
                                    className={`${styles.backgroundImageWrapper} background-image-wrapper`}
                                    style={canvasStyle}
                                >
                                    <ImageDisplay
                                        model={backgroundImageModel}
                                        reportReady={this.onBackgroundImageReady}
                                        autoScale
                                    />
                                </div> : null
                        }
                        {
                            this.containerElementsAreReady() ? this.renderGapChoicesOnCanvas() : null
                        }
                        {
                            model.associableHotspots.map((dropArea) => {
                                const dimensions = dropArea.coords.split(',');
                                return (
                                    <AssociableHotspot
                                        correctResponses={correctResponses}
                                        dimensions={dimensions}
                                        identifier={dropArea.identifier}
                                        idPrefix={this.getIdPrefix()}
                                        key={dropArea.identifier}
                                        model={model}
                                    />
                                );
                            })
                        }
                    </div>
                    {
                        this.shouldIncludeGapChoicesContainer() ? this.renderGapChoicesContainer() : null
                    }
                </div>
            </div>
        );
    }
}

export default GraphicGapMatchInteraction;
